import { openDB } from 'idb';
import { DATA_VERSION } from './version';

export class DataManager {
  constructor(accountId) {
    this.modelsToSync = ['inbox', 'label', 'team'];
    this.accountId = accountId;
    this.db = null;
  }

  async initDb() {
    if (this.db) return this.db;
    const dbName = `cw-store-${this.accountId}`;

    // Wrap openDB in a guarded promise that rejects if:
    //   - the connection is blocked by another tab holding a version-upgrade lock
    //   - IDB takes more than 3 seconds to open (defensive timeout)
    // In either case the caller's try/catch falls back to a plain network request.
    const db = await new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        reject(new Error(`[IDB] openDB timed out for ${dbName}`));
      }, 3000);

      openDB(dbName, DATA_VERSION, {
        upgrade(database) {
          if (!database.objectStoreNames.contains('cache-keys')) {
            database.createObjectStore('cache-keys');
          }
          ['inbox', 'label', 'team'].forEach(store => {
            if (!database.objectStoreNames.contains(store)) {
              database.createObjectStore(store, { keyPath: 'id' });
            }
          });
        },
        // Fires when another open connection is preventing this upgrade.
        // Reject immediately so the caller falls back to the network.
        blocked() {
          clearTimeout(timer);
          reject(new Error(`[IDB] openDB blocked for ${dbName}`));
        },
      })
        .then(result => {
          clearTimeout(timer);
          resolve(result);
        })
        .catch(err => {
          clearTimeout(timer);
          reject(err);
        });
    });

    this.db = db;

    // Store the database name in LocalStorage
    const dbNames = JSON.parse(localStorage.getItem('cw-idb-names') || '[]');
    if (!dbNames.includes(dbName)) {
      dbNames.push(dbName);
      localStorage.setItem('cw-idb-names', JSON.stringify(dbNames));
    }

    return this.db;
  }

  validateModel(name) {
    if (!name) throw new Error('Model name is not defined');
    if (!this.modelsToSync.includes(name)) {
      throw new Error(`Model ${name} is not defined`);
    }
    return true;
  }

  async replace({ modelName, data }) {
    this.validateModel(modelName);

    this.db.clear(modelName);
    return this.push({ modelName, data });
  }

  async push({ modelName, data }) {
    this.validateModel(modelName);

    if (Array.isArray(data)) {
      const tx = this.db.transaction(modelName, 'readwrite');
      data.forEach(item => {
        tx.store.add(item);
      });
      await tx.done;
    } else {
      await this.db.add(modelName, data);
    }
  }

  async get({ modelName }) {
    this.validateModel(modelName);
    return this.db.getAll(modelName);
  }

  async setCacheKeys(cacheKeys) {
    Object.keys(cacheKeys).forEach(async modelName => {
      this.db.put('cache-keys', cacheKeys[modelName], modelName);
    });
  }

  async getCacheKey(modelName) {
    this.validateModel(modelName);

    return this.db.get('cache-keys', modelName);
  }
}
